import React from 'react';
import { useQuery, useMutation } from '@apollo/client';

import { Button, Container, Grid, Paper, Typography, makeStyles } from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Alert from '@material-ui/lab/Alert';
import { useAuth0 } from '@auth0/auth0-react';

import { UserMapper } from 'mappers/UserMapper';
import { RecordingFlag } from 'types/schema.type';
import { GET_RECORDINGS_EXTERNAL_ANNOTATING } from '../queries';
import { UPDATE_RECORDING } from '../mutations';
import { RecordingChart } from './RecordingChart';
import { LoadingSpinner } from './LoadingSpinner';

import { buildExternalAnnotatingRecordingList } from '../utilities';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  buttonStart: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  buttonMark: {
    margin: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(12),
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chart: {
    minWidth: 1200,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  listContainerGrid: {
    maxHeight: `calc(100vh - ${theme.spacing(12)}px)`,
    overflowY: 'auto',
  },
  scrollableListPaper: {
    maxHeight: '550px', // Or another desired height
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stickySubheader: {
    // Ensure the subheader sticks to the top of its scrolling container
    position: 'sticky',
    top: 0,
    // Set background color to match the Paper to hide items scrolling underneath
    backgroundColor: theme.palette.background.paper,
    // Ensure the subheader is layered above the list items
    zIndex: 1,
  },
}));

export function AnnotatorView() {
  const [selectedRecordingId, setSelectedRecordingId] = React.useState(null);

  const { user: currentUser } = useAuth0();

  const user = UserMapper.toDto(currentUser);

  const classes = useStyles();

  const { loading, error, data } = useQuery(GET_RECORDINGS_EXTERNAL_ANNOTATING);
  const [updateRecording] = useMutation(UPDATE_RECORDING);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleSelectRecording = (recordingId) => () => setSelectedRecordingId(recordingId);

  const recordings = data?.getRecordingsForExternalAnnotating ?? [];

  const recordingList = buildExternalAnnotatingRecordingList(recordings);

  const selectedRecording = recordings.find((it) => it.id === selectedRecordingId);

  function handleStartAnnotation() {
    const externalAnnotationMetadata = {
      startedAt: Date.now(),
      startedBy: user.id,
    };

    updateRecording({
      variables: {
        updateRecordingId: selectedRecordingId,
        updateRecordingInput: {
          externalAnnotationMetadata,
        },
      },
      refetchQueries: [{ query: GET_RECORDINGS_EXTERNAL_ANNOTATING }],
    });
  }

  function handleMarkAsCompleted() {
    const { externalAnnotationMetadata } = selectedRecording;

    if (!externalAnnotationMetadata) return null;

    const currentFlags = selectedRecording?.flags ?? [];
    // Remove FOR_EXTERNAL_ANNOTATING and add EXTERNALLY_ANNOTATED
    const newFlags = currentFlags
      .filter((flag) => flag !== RecordingFlag.ForExternalAnnotating)
      .concat(RecordingFlag.ExternallyAnnotated);

    const update = {
      flags: newFlags,
      externalAnnotationMetadata: {
        startedAt: externalAnnotationMetadata.startedAt || Date.now(),
        startedBy: externalAnnotationMetadata.startedBy || user.id,
        completedAt: Date.now(),
        completedBy: user.id,
      },
    };

    return updateRecording({
      variables: {
        updateRecordingId: selectedRecordingId,
        updateRecordingInput: update,
      },
      refetchQueries: [{ query: GET_RECORDINGS_EXTERNAL_ANNOTATING }],
    });
  }

  const recordingFlags = selectedRecording?.flags ?? [];
  const hasForExternalAnnotating = recordingFlags.includes(RecordingFlag.ForExternalAnnotating);
  const hasExternallyAnnotated = recordingFlags.includes(RecordingFlag.ExternallyAnnotated);

  // Check if annotation was restarted (startedAt is more recent than completedAt)
  const startedAt = selectedRecording?.externalAnnotationMetadata?.startedAt;
  const completedAt = selectedRecording?.externalAnnotationMetadata?.completedAt;
  const wasRestarted = startedAt && completedAt && startedAt > completedAt;

  // Available for re-annotation: was previously completed and not restarted yet
  const isAvailableForReannotation =
    hasForExternalAnnotating && !hasExternallyAnnotated && completedAt && !wasRestarted;

  const hasStarted = Boolean(startedAt) && !isAvailableForReannotation;
  const markedAsCompleted = Boolean(completedAt) && !isAvailableForReannotation;

  return (
    <Container maxWidth={false}>
      <Grid spacing={2} container>
        <Grid xs={2} className={classes.listContainerGrid} item>
          <Paper className={classes.paper}>
            <List
              component="nav"
              aria-label="available recordings"
              subheader={
                <ListSubheader component="div" id="available-list-subheader">
                  Available
                </ListSubheader>
              }
              dense
            >
              {recordingList.available.map((item) => (
                <ListItem
                  key={item.id}
                  selected={item.id === selectedRecordingId}
                  button
                  onClick={handleSelectRecording(item.id)}
                >
                  <ListItemText primary={item.fileName} />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Paper className={classes.paper}>
            <List
              component="nav"
              aria-label="in review recordings"
              subheader={
                <ListSubheader component="div" id="in-review-list-subheader">
                  In review
                </ListSubheader>
              }
              dense
            >
              {recordingList.review.map((item) => (
                <ListItem
                  key={item.id}
                  selected={item.id === selectedRecordingId}
                  button
                  onClick={handleSelectRecording(item.id)}
                >
                  <ListItemText primary={item.fileName} />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Paper className={classes.scrollableListPaper}>
            <List
              component="nav"
              aria-label="completed recordings"
              subheader={
                <ListSubheader
                  component="div"
                  id="completed-list-subheader"
                  className={classes.stickySubheader}
                >
                  Completed
                </ListSubheader>
              }
              dense
            >
              {recordingList.completed.map((item) => (
                <ListItem
                  key={item.id}
                  selected={item.id === selectedRecordingId}
                  button
                  onClick={handleSelectRecording(item.id)}
                >
                  <ListItemText primary={item.fileName} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid xs={10} item>
          {selectedRecordingId ? (
            <>
              <Grid direction="row" alignItems="center" container>
                <Button
                  className={classes.buttonStart}
                  variant="contained"
                  color="primary"
                  disabled={hasStarted}
                  onClick={handleStartAnnotation}
                >
                  Start annotation
                </Button>
                <Button
                  className={classes.buttonMark}
                  variant="contained"
                  color="primary"
                  disabled={!hasStarted || markedAsCompleted}
                  onClick={handleMarkAsCompleted}
                >
                  Mark as completed
                </Button>
              </Grid>
              {markedAsCompleted && (
                <Alert severity="info">
                  This recording was marked as completed after annotation
                </Alert>
              )}
              <RecordingChart recordingId={selectedRecordingId} />
            </>
          ) : (
            <Typography variant="h5" gutterBottom>
              Select recording to annotate from the available list
            </Typography>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
